import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SuccessModal from "../components/SuccessModal";
import { AuthContext } from "../context/AuthContext";
import axiosInstance from "../utils/axiosInstance";
import {
  Alert,
  Field,
  Input,
  Page,
  PageHeader,
  PageLoader,
  Panel,
  Spinner,
  Textarea,
  buttonClass,
} from "../components/ui";

const CreateTicket = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [createTicketLoading, setCreateTicketLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [successModal, setSuccessModal] = useState(false);
  const [ticket, setTicket] = useState({
    fileId: "",
    ticketId: "",
    subject: "",
    message: "",
    attachment: null,
  });
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTicket({
      ...ticket,
      [name]: value,
    });
  };

  useEffect(() => {
    const fetchFile = async () => {
      try {
        const response = await axiosInstance.get(`/files/${id}`);
        setTicket((prevTicket) => ({
          ...prevTicket,
          fileId: response.data.file.id,
          ticketId: response.data.file.file_id,
          subject: "Ticket ID:" + response.data.file.file_id,
        }));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchFile();
  }, [id]);

  const handleFileChange = (e) => {
    setTicket({
      ...ticket,
      attachment: e.target.files[0],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreateTicketLoading(true);

    const formData = new FormData();
    formData.append("jsonData", JSON.stringify(ticket));

    if (ticket.attachment) {
      formData.append("file", ticket.attachment);
    }

    try {
      await axiosInstance.post("/tickets", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setCreateTicketLoading(false);
      setSuccessModal(true);
      setTimeout(() => {
        setSuccessModal(false);
        if (user.role.toUpperCase() === "ADMIN") {
          navigate(`/admin/view-file/${id}`);
        } else {
          navigate(`/view-file/${id}`);
        }
      }, 3000);
    } catch (error) {
      setCreateTicketLoading(false);
      if (
        error.response &&
        error.response.status &&
        error.response.status === 400
      ) {
        setErrorMessage(error.response.data.message);
        return;
      }
      setErrorMessage("Something went wrong");
    }
  };

  if (loading) {
    return (
      <Page width="max-w-2xl">
        <PageHeader title="Create New Ticket" onBack={() => window.history.back()} />
        <PageLoader />
      </Page>
    );
  }

  return (
    <Page width="max-w-2xl">
      <PageHeader
        title="Create New Ticket"
        subtitle="Our admins answer during working hours."
        onBack={() => window.history.back()}
      />

      {errorMessage ? <Alert tone="error">{errorMessage}</Alert> : null}

      <Panel>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Field label="Subject">
            <Input
              type="text"
              name="subject"
              value={ticket.subject}
              onChange={handleChange}
              disabled
              readOnly
              required
            />
          </Field>

          <Field label="Message" required>
            <Textarea
              name="message"
              rows={6}
              value={ticket.message}
              onChange={handleChange}
              placeholder="Describe what you need help with…"
              required
            />
          </Field>

          <Field label="Attachment" hint="Optional — a log, a screenshot or the original file.">
            <input
              type="file"
              name="attachment"
              onChange={handleFileChange}
              className="block w-full cursor-pointer rounded-[12px] border border-gray-300 bg-white text-sm text-gray-600 file:mr-3 file:cursor-pointer file:rounded-l-[10px] file:border-0 file:bg-gray-100 file:px-4 file:py-2.5 file:text-sm file:font-semibold file:text-gray-700 hover:border-red-400/60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-300 dark:file:bg-[#303032] dark:file:text-slate-200"
            />
          </Field>

          <div className="flex justify-end">
            <button type="submit" disabled={createTicketLoading} className={buttonClass()}>
              {createTicketLoading ? <Spinner /> : null}
              {createTicketLoading ? "Creating…" : "Create Ticket"}
            </button>
          </div>
        </form>
      </Panel>

      {successModal && (
        <SuccessModal message={"Ticket is created successfully"} isModal={true} />
      )}
    </Page>
  );
};

export default CreateTicket;
